<template>
  <div class="engagement-dashboard">
    <!-- Header -->
    <div class="dashboard-header">
      <div>
        <h2 class="dashboard-title">
          <i class="fas fa-chart-line"></i>
          Learner Engagement
        </h2>
        <p class="dashboard-subtitle">
          RFM analytics: Recency · Frequency · Momentum — powered by real learning signals
        </p>
      </div>
      <div class="header-actions">
        <button class="btn-refresh" :disabled="loading" @click="loadOverview">
          <i class="fas fa-sync-alt" :class="{ spinning: loading }"></i>
          Refresh
        </button>
        <button
          v-if="canRunPipeline"
          class="btn-run-pipeline"
          @click="runPipeline"
          :disabled="pipelineRunning"
        >
          <i class="fas fa-play-circle"></i>
          {{ pipelineRunning ? 'Running…' : 'Compute Now' }}
        </button>
      </div>
    </div>

    <!-- Error state -->
    <div v-if="error" class="error-banner">
      <i class="fas fa-exclamation-triangle"></i>
      {{ error }}
    </div>

    <div v-if="overview?.pipeline?.needs_compute" class="pipeline-banner">
      <div class="pipeline-banner__body">
        <i class="fas fa-info-circle"></i>
        <div>
          <strong>Scores not computed yet.</strong>
          You have {{ overview.pipeline.enrolled_learners.toLocaleString() }} enrolled learners
          but no RFM scores in the database.
          Click <em>Compute Now</em> to generate segments and populate the table
          (runs inline, no Celery required).
        </div>
      </div>
      <button
        v-if="canRunPipeline"
        class="btn-run-pipeline btn-run-pipeline--inline"
        @click="runPipeline"
        :disabled="pipelineRunning"
      >
        {{ pipelineRunning ? 'Computing…' : 'Compute Now' }}
      </button>
    </div>

    <div
      v-else-if="overview?.pipeline?.no_enrollments && overview?.risk?.total_scored === 0"
      class="pipeline-banner pipeline-banner--warn"
    >
      <div class="pipeline-banner__body">
        <i class="fas fa-info-circle"></i>
        <div>
          <strong>No enrolled learners yet.</strong>
          RFM scores are computed from course enrollments. Once learners enroll,
          click <em>Compute Now</em> to populate this dashboard.
        </div>
      </div>
    </div>

    <div v-if="pipelineMessage" class="success-banner">
      <i class="fas fa-check-circle"></i>
      {{ pipelineMessage }}
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading && !overview" class="skeleton-grid">
      <div v-for="n in 4" :key="n" class="skeleton-card"></div>
    </div>

    <template v-if="overview">
      <!-- KPI Cards Row -->
      <div class="kpi-grid">
        <div class="kpi-card">
          <div class="kpi-icon kpi-icon--blue">
            <i class="fas fa-users"></i>
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ overview.activity.mau.toLocaleString() }}</div>
            <div class="kpi-label">Monthly Active Users</div>
            <div class="kpi-sub">
              <span class="kpi-dau">{{ overview.activity.dau }} today</span>
              <span class="kpi-sep">·</span>
              <span class="kpi-wau">{{ overview.activity.wau }} this week</span>
            </div>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon kpi-icon--green">
            <i class="fas fa-percent"></i>
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ overview.activity.stickiness_pct }}%</div>
            <div class="kpi-label">Stickiness (DAU/MAU)</div>
            <div class="kpi-sub">
              <span :class="stickinessClass">{{ stickinessLabel }}</span>
            </div>
          </div>
        </div>

        <div class="kpi-card" :class="{ 'kpi-card--alert': overview.risk.at_risk_count > 0 }">
          <div class="kpi-icon kpi-icon--orange">
            <i class="fas fa-exclamation-circle"></i>
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ overview.risk.at_risk_count }}</div>
            <div class="kpi-label">At-Risk Learners</div>
            <div class="kpi-sub">
              <span class="kpi-pct">{{ overview.risk.at_risk_pct }}% of scored users</span>
            </div>
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-icon kpi-icon--purple">
            <i class="fas fa-fire"></i>
          </div>
          <div class="kpi-body">
            <div class="kpi-value">{{ overview.streaks.users_on_3d_streak }}</div>
            <div class="kpi-label">Active Streaks (3+ days)</div>
            <div class="kpi-sub">
              <span>Avg {{ overview.streaks.avg_streak_days }}d · Max {{ overview.streaks.max_streak_days }}d</span>
            </div>
          </div>
        </div>
      </div>

      <!-- RFM Segment Distribution + At-Risk List -->
      <div class="analytics-grid">
        <!-- Segment Distribution -->
        <div class="analytics-card segment-card">
          <h3 class="card-title">
            <i class="fas fa-layer-group"></i>
            RFM Segments
          </h3>
          <div class="segment-list">
            <div
              v-for="seg in segmentList"
              :key="seg.key"
              class="segment-row"
              @click="filterBySegment(seg.key)"
            >
              <div class="segment-dot" :style="{ background: seg.color }"></div>
              <div class="segment-name">{{ seg.label }}</div>
              <div class="segment-bar-wrap">
                <div
                  class="segment-bar"
                  :style="{ width: segmentBarWidth(seg.count) + '%', background: seg.color }"
                ></div>
              </div>
              <div class="segment-count">{{ seg.count || 0 }}</div>
            </div>
          </div>
          <p class="card-hint">Click a segment to filter the user table below</p>
        </div>

        <!-- At-Risk Alert List -->
        <div class="analytics-card atrisk-card">
          <h3 class="card-title">
            <i class="fas fa-bell"></i>
            At-Risk Learners
            <span class="badge-count" v-if="overview.risk.at_risk_count > 0">
              {{ overview.risk.at_risk_count }}
            </span>
          </h3>
          <div v-if="atRiskUsers.length === 0 && !loadingUsers" class="empty-state-small">
            <i class="fas fa-check-circle"></i> No at-risk learners right now.
          </div>
          <div class="atrisk-list">
            <div
              v-for="user in atRiskUsers.slice(0, 8)"
              :key="user.user_id"
              class="atrisk-row"
              @click="openUserDetail(user)"
            >
              <div class="user-avatar">{{ userInitials(user) }}</div>
              <div class="user-info">
                <div class="user-name">{{ user.full_name || user.email }}</div>
                <div class="user-reasons">
                  <span
                    v-for="reason in user.at_risk_reasons"
                    :key="reason"
                    class="reason-tag"
                    :title="reasonLabel(reason)"
                  >
                    {{ reasonLabel(reason) }}
                  </span>
                </div>
              </div>
              <div class="user-meta">
                <div class="rfm-badge" :class="segmentClass(user.rfm_segment)">
                  {{ user.rfm_segment }}
                </div>
                <div class="days-ago">{{ user.days_since_last_login }}d ago</div>
              </div>
            </div>
          </div>
          <button
            v-if="overview.risk.at_risk_count > 8"
            class="btn-view-all"
            @click="filterByAtRisk"
          >
            View all {{ overview.risk.at_risk_count }} at-risk →
          </button>
        </div>
      </div>

      <!-- Inline User Engagement Table -->
      <UserEngagementTable
        ref="userTable"
        :initial-segment="activeSegmentFilter"
        :initial-at-risk="atRiskFilter"
        @user-click="openUserDetail"
      />

    </template>

    <!-- User Detail Modal -->
    <UserEngagementDetail
      v-if="selectedUser"
      :user-id="selectedUser.user_id"
      @close="selectedUser = null"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import api from '@/services/api'
import auth from '@/services/auth'
import UserEngagementTable from './UserEngagementTable.vue'
import UserEngagementDetail from './UserEngagementDetail.vue'

const SEGMENT_CONFIG = [
  { key: 'CHAMPION',  label: 'Champions',      color: '#7c3aed' },
  { key: 'LOYAL',     label: 'Loyal Learners',  color: '#2563eb' },
  { key: 'PROMISING', label: 'Promising',       color: '#0891b2' },
  { key: 'NEW',       label: 'New Learners',    color: '#16a34a' },
  { key: 'CASUAL',    label: 'Casual',          color: '#6b7280' },
  { key: 'AT_RISK',   label: 'At Risk',         color: '#ea580c' },
  { key: 'DORMANT',   label: 'Dormant',         color: '#dc2626' },
]

const REASON_LABELS = {
  dormant_with_active_enrollment: 'Inactive (14d+)',
  session_frequency_drop: 'Engagement drop',
  early_dropout_signal: 'Early dropout',
  quiz_discouragement: 'Quiz struggles',
}

export default {
  name: 'EngagementDashboard',
  components: { UserEngagementTable, UserEngagementDetail },

  setup() {
    const loading = ref(false)
    const loadingUsers = ref(false)
    const pipelineRunning = ref(false)
    const error = ref(null)
    const pipelineMessage = ref(null)
    const overview = ref(null)
    const atRiskUsers = ref([])
    const selectedUser = ref(null)
    const activeSegmentFilter = ref(null)
    const atRiskFilter = ref(false)
    const userTable = ref(null)
    const canRunPipeline = computed(() => auth.isLmsAdmin())

    const segmentList = computed(() => {
      if (!overview.value) return []
      return SEGMENT_CONFIG.map(s => ({
        ...s,
        count: overview.value.segments[s.key] || 0,
      }))
    })

    const totalSegmented = computed(() =>
      segmentList.value.reduce((sum, s) => sum + s.count, 0)
    )

    const segmentBarWidth = (count) =>
      totalSegmented.value ? Math.max((count / totalSegmented.value) * 100, 2) : 0

    const stickinessLabel = computed(() => {
      const pct = overview.value?.activity?.stickiness_pct || 0
      if (pct >= 20) return '🔥 Excellent (Facebook-tier)'
      if (pct >= 10) return '✅ Good (Udemy-tier)'
      if (pct >= 5)  return '⚠️ Fair — needs growth'
      return '🔴 Low — priority focus'
    })

    const stickinessClass = computed(() => {
      const pct = overview.value?.activity?.stickiness_pct || 0
      if (pct >= 20) return 'kpi-trend-up'
      if (pct >= 10) return 'kpi-trend-ok'
      return 'kpi-trend-down'
    })

    const loadOverview = async () => {
      loading.value = true
      error.value = null
      try {
        const res = await api.get('/lms/engagement/overview/')
        overview.value = res.data
      } catch (e) {
        const status = e.response?.status
        if (status === 403) {
          error.value = 'You do not have permission to view engagement analytics.'
        } else {
          error.value = 'Failed to load engagement data. Please try again.'
        }
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const loadAtRiskUsers = async () => {
      loadingUsers.value = true
      try {
        const res = await api.get('/lms/engagement/users/', {
          params: { at_risk: 'true', limit: 10 }
        })
        atRiskUsers.value = res.data.results || []
      } catch (e) {
        console.error('Failed to load at-risk users', e)
      } finally {
        loadingUsers.value = false
      }
    }

    const runPipeline = async () => {
      pipelineRunning.value = true
      error.value = null
      pipelineMessage.value = null
      try {
        const res = await api.post('/lms/engagement/admin/run-now/', { sync: true })
        const scored = res.data?.scored ?? 0
        const errors = res.data?.errors ?? 0
        if (scored > 0) {
          pipelineMessage.value = `Computed engagement scores for ${scored.toLocaleString()} learners.`
        } else if (errors > 0) {
          pipelineMessage.value = `Pipeline failed for ${errors} learner(s). Check backend logs.`
        } else {
          pipelineMessage.value = 'Pipeline finished but no enrolled learners were scored.'
        }
        atRiskFilter.value = false
        activeSegmentFilter.value = null
        userTable.value?.applyFilter({ segment: null, at_risk: false })
        await loadOverview()
        await loadAtRiskUsers()
        userTable.value?.loadUsers(true)
      } catch (e) {
        const status = e.response?.status
        if (status === 403) {
          error.value = 'You do not have permission to run the engagement pipeline.'
        } else {
          error.value = 'Pipeline failed. Try again or contact support.'
        }
      } finally {
        pipelineRunning.value = false
      }
    }

    const filterBySegment = (segmentKey) => {
      activeSegmentFilter.value = segmentKey
      atRiskFilter.value = false
      userTable.value?.applyFilter({ segment: segmentKey, at_risk: false })
    }

    const filterByAtRisk = () => {
      atRiskFilter.value = true
      activeSegmentFilter.value = null
      userTable.value?.applyFilter({ at_risk: true, segment: null })
    }

    const openUserDetail = (user) => {
      selectedUser.value = user
    }

    const userInitials = (user) => {
      const name = user.full_name || user.email || '?'
      return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    }

    const segmentClass = (segment) => `seg--${(segment || 'casual').toLowerCase()}`

    const reasonLabel = (reason) => REASON_LABELS[reason] || reason

    onMounted(async () => {
      await loadOverview()
      await loadAtRiskUsers()
    })

    return {
      loading, loadingUsers, pipelineRunning, error, pipelineMessage,
      overview, atRiskUsers, selectedUser,
      activeSegmentFilter, atRiskFilter, userTable, canRunPipeline,
      segmentList, segmentBarWidth, stickinessLabel, stickinessClass,
      loadOverview, runPipeline, filterBySegment, filterByAtRisk,
      openUserDetail, userInitials, segmentClass, reasonLabel,
    }
  }
}
</script>

<style scoped>
.engagement-dashboard {
  padding: 1.5rem;
  min-height: 600px;
}

.dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.dashboard-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.25rem 0;
}

.dashboard-title i { color: #7c3aed; margin-right: 0.5rem; }

.dashboard-subtitle {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
}

.header-actions { display: flex; gap: 0.75rem; align-items: center; }

.btn-refresh, .btn-run-pipeline {
  display: flex; align-items: center; gap: 0.4rem;
  padding: 0.5rem 1rem; border-radius: 8px; font-size: 0.875rem;
  font-weight: 500; cursor: pointer; border: none; transition: all 0.15s;
}

.btn-refresh {
  background: #f1f5f9; color: #475569;
}
.btn-refresh:hover { background: #e2e8f0; }

.btn-run-pipeline {
  background: #7c3aed; color: white;
}
.btn-run-pipeline:hover { background: #6d28d9; }
.btn-run-pipeline:disabled, .btn-refresh:disabled { opacity: 0.6; cursor: not-allowed; }

.error-banner {
  background: #fef2f2; border: 1px solid #fecaca; color: #dc2626;
  padding: 0.75rem 1rem; border-radius: 8px; margin-bottom: 1rem;
  font-size: 0.875rem;
}
.error-banner i { margin-right: 0.5rem; }

.pipeline-banner {
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af;
  padding: 0.875rem 1rem; border-radius: 8px; margin-bottom: 1rem;
  font-size: 0.875rem; flex-wrap: wrap;
}
.pipeline-banner__body { display: flex; gap: 0.6rem; align-items: flex-start; flex: 1; }
.pipeline-banner__body i { margin-top: 0.15rem; flex-shrink: 0; }
.pipeline-banner--warn { background: #fffbeb; border-color: #fde68a; color: #92400e; }
.btn-run-pipeline--inline { padding: 0.45rem 0.9rem; font-size: 0.8rem; white-space: nowrap; }

.success-banner {
  background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534;
  padding: 0.75rem 1rem; border-radius: 8px; margin-bottom: 1rem;
  font-size: 0.875rem;
}
.success-banner i { margin-right: 0.5rem; }

/* Skeleton */
.skeleton-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin-bottom: 1.5rem;
}
.skeleton-card {
  height: 120px; background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 12px;
}
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* KPI Cards */
.kpi-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin-bottom: 1.5rem;
}

.kpi-card {
  background: white; border-radius: 12px; border: 1px solid #e2e8f0;
  padding: 1.25rem; display: flex; align-items: flex-start; gap: 1rem;
  transition: box-shadow 0.15s;
}
.kpi-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
.kpi-card--alert { border-color: #fed7aa; background: #fff7ed; }

.kpi-icon {
  width: 44px; height: 44px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem; flex-shrink: 0;
}
.kpi-icon--blue  { background: #dbeafe; color: #2563eb; }
.kpi-icon--green { background: #dcfce7; color: #16a34a; }
.kpi-icon--orange { background: #fed7aa; color: #ea580c; }
.kpi-icon--purple { background: #ede9fe; color: #7c3aed; }

.kpi-value {
  font-size: 1.75rem; font-weight: 700; color: #1e293b; line-height: 1;
}
.kpi-label { font-size: 0.8rem; color: #64748b; margin: 0.25rem 0; font-weight: 500; }
.kpi-sub { font-size: 0.75rem; color: #94a3b8; display: flex; gap: 0.4rem; flex-wrap: wrap; }

.kpi-trend-up   { color: #16a34a; font-weight: 600; }
.kpi-trend-ok   { color: #2563eb; font-weight: 600; }
.kpi-trend-down { color: #dc2626; font-weight: 600; }

/* Analytics grid */
.analytics-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1.5rem;
}

.analytics-card {
  background: white; border-radius: 12px; border: 1px solid #e2e8f0; padding: 1.25rem;
}

.card-title {
  font-size: 0.95rem; font-weight: 600; color: #374151; margin: 0 0 1rem 0;
  display: flex; align-items: center; gap: 0.5rem;
}
.card-title i { color: #7c3aed; }

/* Segment list */
.segment-list { display: flex; flex-direction: column; gap: 0.6rem; }
.segment-row {
  display: flex; align-items: center; gap: 0.6rem; cursor: pointer;
  padding: 0.3rem; border-radius: 6px; transition: background 0.1s;
}
.segment-row:hover { background: #f8fafc; }
.segment-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.segment-name { width: 110px; font-size: 0.8rem; color: #374151; font-weight: 500; }
.segment-bar-wrap { flex: 1; height: 8px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
.segment-bar { height: 100%; border-radius: 4px; transition: width 0.5s ease; }
.segment-count { width: 30px; text-align: right; font-size: 0.8rem; font-weight: 600; color: #374151; }
.card-hint { font-size: 0.72rem; color: #94a3b8; margin: 0.75rem 0 0 0; }

/* At-risk list */
.atrisk-list { display: flex; flex-direction: column; gap: 0.5rem; }
.atrisk-row {
  display: flex; align-items: center; gap: 0.75rem; padding: 0.6rem 0.5rem;
  border-radius: 8px; cursor: pointer; transition: background 0.1s;
}
.atrisk-row:hover { background: #fff7ed; }

.user-avatar {
  width: 36px; height: 36px; border-radius: 50%; background: #7c3aed;
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 0.7rem; font-weight: 700; flex-shrink: 0;
}

.user-info { flex: 1; min-width: 0; }
.user-name { font-size: 0.82rem; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.user-reasons { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.2rem; }
.reason-tag {
  font-size: 0.65rem; background: #fef3c7; color: #92400e;
  padding: 0.1rem 0.35rem; border-radius: 3px; font-weight: 500;
}

.user-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 0.2rem; }
.days-ago { font-size: 0.7rem; color: #94a3b8; }
.rfm-badge {
  font-size: 0.6rem; font-weight: 700; padding: 0.1rem 0.4rem;
  border-radius: 10px; text-transform: uppercase; letter-spacing: 0.04em;
}
.seg--champion  { background: #ede9fe; color: #5b21b6; }
.seg--loyal     { background: #dbeafe; color: #1d4ed8; }
.seg--promising { background: #cffafe; color: #0e7490; }
.seg--new       { background: #dcfce7; color: #166534; }
.seg--casual    { background: #f1f5f9; color: #475569; }
.seg--at_risk   { background: #ffedd5; color: #c2410c; }
.seg--dormant   { background: #fee2e2; color: #b91c1c; }

.badge-count {
  background: #ea580c; color: white; font-size: 0.65rem; font-weight: 700;
  padding: 0.1rem 0.45rem; border-radius: 10px; margin-left: 0.5rem;
}

.btn-view-all {
  width: 100%; margin-top: 0.75rem; padding: 0.5rem;
  background: none; border: 1px solid #e2e8f0; border-radius: 8px;
  color: #7c3aed; font-size: 0.8rem; font-weight: 600; cursor: pointer;
  transition: all 0.15s;
}
.btn-view-all:hover { background: #ede9fe; border-color: #7c3aed; }

.empty-state-small { padding: 1.5rem; text-align: center; color: #16a34a; font-size: 0.875rem; }

.spinning { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 1024px) {
  .kpi-grid { grid-template-columns: repeat(2, 1fr); }
  .analytics-grid { grid-template-columns: 1fr; }
}

@media (max-width: 600px) {
  .kpi-grid { grid-template-columns: 1fr; }
}
</style>
